import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import {
    createIdempotencyKey,
    getSpeechCalibrationBlindReview,
    getSpeechCalibrationComparison,
    SpeechCalibrationApiError,
    submitSpeechCalibrationBlindRating,
    submitSpeechCalibrationComparison,
    type SpeechCalibrationBlindReview,
    type SpeechCalibrationComparison,
    type SpeechCalibrationConfidence,
    type SpeechCalibrationPhoneVerdict,
    type SpeechCalibrationTrait,
    type SpeechCalibrationVerdict,
} from '../admin/adminSpeechCalibrationApi';
import { MediaPreview } from '../examTasks/MediaPreview';
import {
    calibrationTraitLabels,
    calibrationTraits,
    confidenceOptions,
    formatDuration,
} from './speechCalibrationConstants';
import { TraitScoreSlider } from './speechCalibrationUi';

const reviewFlags = [
    { value: 'not_assessable', label: 'Not assessable' },
    { value: 'audio_unclear', label: 'Audio is unclear' },
    { value: 'valid_accent', label: 'Valid accent variation' },
    { value: 'requires_senior_review', label: 'Needs senior review' },
];

const traitVerdicts: Array<{ value: SpeechCalibrationVerdict; label: string }> = [
    { value: 'much_too_low', label: 'Model is much too low' },
    { value: 'too_low', label: 'Model is too low' },
    { value: 'about_right', label: 'About right' },
    { value: 'too_high', label: 'Model is too high' },
    { value: 'much_too_high', label: 'Model is much too high' },
    { value: 'cannot_judge', label: 'Cannot judge' },
];

const phoneVerdicts: Array<{ value: SpeechCalibrationPhoneVerdict; label: string }> = [
    { value: 'correct', label: 'Correction is correct' },
    { value: 'wrong', label: 'Correction is wrong' },
    { value: 'valid_accent_variant', label: 'Valid accent variant' },
    { value: 'not_sure', label: 'Not sure' },
];

function makeTraitRecord<T>(value: T) {
    return Object.fromEntries(calibrationTraits.map((trait) => [trait, value])) as Record<SpeechCalibrationTrait, T>;
}

export function SpeechCalibrationReviewPage() {
    const { assignmentId = '' } = useParams();
    const navigate = useNavigate();
    const [review, setReview] = useState<SpeechCalibrationBlindReview | null>(null);
    const [comparison, setComparison] = useState<SpeechCalibrationComparison | null>(null);
    const [scores, setScores] = useState<Record<SpeechCalibrationTrait, number>>(makeTraitRecord(2.5));
    const [touchedTraits, setTouchedTraits] = useState<Set<SpeechCalibrationTrait>>(new Set());
    const [confidence, setConfidence] = useState<Record<SpeechCalibrationTrait, SpeechCalibrationConfidence>>(
        makeTraitRecord('medium'),
    );
    const [flags, setFlags] = useState<string[]>([]);
    const [blindNotes, setBlindNotes] = useState('');
    const [traitDecisions, setTraitDecisions] = useState<Partial<Record<SpeechCalibrationTrait, SpeechCalibrationVerdict>>>({});
    const [phoneDecisions, setPhoneDecisions] = useState<Record<string, SpeechCalibrationPhoneVerdict>>({});
    const [comparisonNotes, setComparisonNotes] = useState('');
    const [blindIdempotencyKey] = useState(() => createIdempotencyKey('blind-rating'));
    const [comparisonIdempotencyKey] = useState(() => createIdempotencyKey('comparison'));
    const [referenceRevealed, setReferenceRevealed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [complete, setComplete] = useState(false);

    const load = useCallback(async () => {
        if (!assignmentId) {
            setError('Assignment ID is missing');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const blindReview = await getSpeechCalibrationBlindReview(assignmentId);
            setReview(blindReview);
            try {
                const existingComparison = await getSpeechCalibrationComparison(assignmentId);
                setComparison(existingComparison);
            } catch (comparisonError) {
                if (comparisonError instanceof SpeechCalibrationApiError && comparisonError.status === 409) {
                    // A 409 is expected until the immutable blind judgment exists.
                    setComparison(null);
                } else {
                    throw comparisonError;
                }
            }
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load this review');
        } finally {
            setIsLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        void load();
    }, [load]);

    const isNotAssessable = flags.includes('not_assessable');
    const blindReady = isNotAssessable || calibrationTraits.every((trait) => touchedTraits.has(trait));
    const comparisonReady = useMemo(() => {
        if (!comparison) return false;
        const allTraits = comparison.traits.every((item) => Boolean(traitDecisions[item.trait]));
        const allPhones = comparison.phoneCandidates.every((item) => Boolean(phoneDecisions[item.candidateId]));
        return allTraits && allPhones;
    }, [comparison, phoneDecisions, traitDecisions]);

    const toggleFlag = (flag: string) => {
        setFlags((current) => current.includes(flag) ? current.filter((item) => item !== flag) : [...current, flag]);
    };

    const submitBlind = async () => {
        if (!assignmentId || !blindReady) {
            setError('Rate all four traits or mark the recording as not assessable.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await submitSpeechCalibrationBlindRating(assignmentId, {
                traits: isNotAssessable
                    ? []
                    : calibrationTraits.map((trait) => ({
                        trait,
                        value: scores[trait],
                        confidence: confidence[trait],
                    })),
                flags,
                notes: blindNotes.trim(),
                idempotencyKey: blindIdempotencyKey,
            });
            const nextComparison = await getSpeechCalibrationComparison(assignmentId);
            setComparison(nextComparison);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (submitError) {
            setError(submitError instanceof Error ? submitError.message : 'Unable to save the blind judgment');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitComparison = async () => {
        if (!assignmentId || !comparison || !comparisonReady) {
            setError('Choose a verdict for every trait and phoneme candidate.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await submitSpeechCalibrationComparison(assignmentId, {
                traits: comparison.traits.map((item) => ({
                    trait: item.trait,
                    verdict: traitDecisions[item.trait] as SpeechCalibrationVerdict,
                })),
                phones: comparison.phoneCandidates.map((item) => ({
                    candidateId: item.candidateId,
                    verdict: phoneDecisions[item.candidateId],
                })),
                notes: comparisonNotes.trim(),
                idempotencyKey: comparisonIdempotencyKey,
            });
            setComplete(true);
        } catch (submitError) {
            setError(submitError instanceof Error ? submitError.message : 'Unable to save the comparison');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 420 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, minWidth: 0 }}>
            <Title title="Speech Calibration Review" />
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/speech-calibration')} sx={{ mb: 1 }}>
                Daily queue
            </Button>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {complete ? (
                <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon />}
                    action={<Button color="inherit" onClick={() => navigate('/speech-calibration')}>Next assignment</Button>}
                    sx={{ mb: 2 }}
                >
                    Your independent rating and model comparison are complete.
                </Alert>
            ) : null}

            {!review ? (
                <Alert severity="warning">This assignment is unavailable.</Alert>
            ) : (
                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <Box>
                                <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 900 }}>
                                    {comparison ? 'Compare the model' : 'Blind human judgment'}
                                </Typography>
                                <Typography sx={{ mt: 0.4, color: '#64748b' }}>
                                    {review.examName} · {review.examTaskType} · Question {review.questionNo ?? '—'} · {formatDuration(review.durationSeconds)}
                                </Typography>
                            </Box>
                            <Chip
                                color={comparison ? 'secondary' : 'primary'}
                                label={comparison ? `Model ${comparison.modelRevision}` : 'AI scores hidden'}
                                sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 800 }}
                            />
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
                        <Typography sx={{ mb: 1.5, color: '#0f172a', fontWeight: 850 }}>1. Listen to the recording</Typography>
                        <MediaPreview
                            url={review.audioUrl}
                            title="Learner response"
                            width="100%"
                            showWaveform
                            mediaKind="audio"
                        />
                        {review.referenceText ? (
                            <Box sx={{ mt: 2 }}>
                                {!referenceRevealed ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => setReferenceRevealed(true)}
                                    >
                                        Reveal prompt or reference text
                                    </Button>
                                ) : (
                                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8fafc' }}>
                                        <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 850, textTransform: 'uppercase' }}>
                                            Prompt / reference
                                        </Typography>
                                        <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{review.referenceText}</Typography>
                                    </Paper>
                                )}
                            </Box>
                        ) : null}
                    </Paper>

                    {!comparison ? (
                        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
                            <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>2. Record your independent judgment</Typography>
                            <Typography sx={{ mt: 0.35, mb: 2, color: '#64748b', fontSize: '0.85rem' }}>
                                Use half-point steps. The experimental model stays hidden until this rating is submitted.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                                    gap: 2.5,
                                }}
                            >
                                {calibrationTraits.map((trait) => (
                                    <Paper key={trait} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <TraitScoreSlider
                                            trait={trait}
                                            value={scores[trait]}
                                            disabled={isNotAssessable}
                                            onChange={(value) => {
                                                setScores((current) => ({ ...current, [trait]: value }));
                                                setTouchedTraits((current) => new Set(current).add(trait));
                                            }}
                                        />
                                        <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
                                            <InputLabel id={`${trait}-confidence-label`}>Confidence</InputLabel>
                                            <Select
                                                labelId={`${trait}-confidence-label`}
                                                label="Confidence"
                                                value={confidence[trait]}
                                                disabled={isNotAssessable}
                                                onChange={(event) => setConfidence((current) => ({
                                                    ...current,
                                                    [trait]: event.target.value as SpeechCalibrationConfidence,
                                                }))}
                                            >
                                                {confidenceOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {!touchedTraits.has(trait) && !isNotAssessable ? (
                                            <Typography sx={{ mt: 0.75, color: '#b45309', fontSize: '0.74rem' }}>Move the slider to confirm this rating.</Typography>
                                        ) : null}
                                    </Paper>
                                ))}
                            </Box>

                            <Divider sx={{ my: 2.5 }} />
                            <Typography sx={{ mb: 0.75, fontWeight: 800 }}>Review flags</Typography>
                            <Stack direction="row" useFlexGap flexWrap="wrap">
                                {reviewFlags.map((item) => (
                                    <FormControlLabel
                                        key={item.value}
                                        control={<Checkbox checked={flags.includes(item.value)} onChange={() => toggleFlag(item.value)} />}
                                        label={item.label}
                                    />
                                ))}
                            </Stack>
                            <TextField
                                label="Private review note"
                                value={blindNotes}
                                onChange={(event) => setBlindNotes(event.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                sx={{ mt: 1.5 }}
                                helperText="Do not enter learner transcripts or personal information."
                            />
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={!blindReady || isSubmitting}
                                    onClick={() => void submitBlind()}
                                    startIcon={isSubmitting ? <CircularProgress size={17} color="inherit" /> : undefined}
                                >
                                    Submit blind judgment
                                </Button>
                            </Stack>
                        </Paper>
                    ) : (
                        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, borderRadius: 2.5 }}>
                            <Typography sx={{ color: '#0f172a', fontWeight: 850 }}>2. Compare after your blind judgment</Typography>
                            <Typography sx={{ mt: 0.35, mb: 2, color: '#64748b', fontSize: '0.85rem' }}>
                                Your original judgment is immutable. Assess whether the model result is technically useful.
                            </Typography>

                            <Stack spacing={1.5}>
                                {comparison.traits.map((item) => (
                                    <Paper key={item.trait} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            alignItems={{ xs: 'stretch', md: 'center' }}
                                            spacing={1.5}
                                        >
                                            <Box sx={{ minWidth: 170 }}>
                                                <Typography sx={{ fontWeight: 850 }}>{calibrationTraitLabels[item.trait]}</Typography>
                                                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                                                    <Chip size="small" color="primary" label={`You: ${item.humanValue.toFixed(1)}`} />
                                                    <Chip size="small" color="secondary" label={`Model: ${item.modelValue?.toFixed(1) ?? '—'}`} />
                                                </Stack>
                                            </Box>
                                            <FormControl size="small" fullWidth>
                                                <InputLabel id={`${item.trait}-verdict-label`}>Verdict</InputLabel>
                                                <Select
                                                    labelId={`${item.trait}-verdict-label`}
                                                    label="Verdict"
                                                    value={traitDecisions[item.trait] ?? ''}
                                                    onChange={(event) => setTraitDecisions((current) => ({
                                                        ...current,
                                                        [item.trait]: event.target.value as SpeechCalibrationVerdict,
                                                    }))}
                                                >
                                                    {traitVerdicts.map((verdict) => (
                                                        <MenuItem key={verdict.value} value={verdict.value}>{verdict.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>

                            {comparison.phoneCandidates.length > 0 ? (
                                <>
                                    <Divider sx={{ my: 2.5 }} />
                                    <Typography sx={{ mb: 1, fontWeight: 850 }}>Phoneme correction checks</Typography>
                                    <Stack spacing={1}>
                                        {comparison.phoneCandidates.map((candidate) => (
                                            <Paper key={candidate.candidateId} variant="outlined" sx={{ p: 1.5 }}>
                                                <Stack
                                                    direction={{ xs: 'column', md: 'row' }}
                                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                                    spacing={1.5}
                                                >
                                                    <Box sx={{ minWidth: 260 }}>
                                                        <Typography sx={{ fontWeight: 800 }}>{candidate.word}</Typography>
                                                        <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                            Target /{candidate.targetPhone}/ · Heard /{candidate.observedPhone}/ · {candidate.startSeconds.toFixed(2)}–{candidate.endSeconds.toFixed(2)}s
                                                        </Typography>
                                                    </Box>
                                                    <FormControl size="small" fullWidth>
                                                        <InputLabel id={`${candidate.candidateId}-phone-verdict-label`}>Correction verdict</InputLabel>
                                                        <Select
                                                            labelId={`${candidate.candidateId}-phone-verdict-label`}
                                                            label="Correction verdict"
                                                            value={phoneDecisions[candidate.candidateId] ?? ''}
                                                            onChange={(event) => setPhoneDecisions((current) => ({
                                                                ...current,
                                                                [candidate.candidateId]: event.target.value as SpeechCalibrationPhoneVerdict,
                                                            }))}
                                                        >
                                                            {phoneVerdicts.map((verdict) => (
                                                                <MenuItem key={verdict.value} value={verdict.value}>{verdict.label}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </>
                            ) : null}

                            <TextField
                                label="Comparison note"
                                value={comparisonNotes}
                                onChange={(event) => setComparisonNotes(event.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                sx={{ mt: 2 }}
                                helperText="Do not enter learner transcripts or personal information."
                            />
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={!comparisonReady || isSubmitting || complete}
                                    onClick={() => void submitComparison()}
                                    startIcon={isSubmitting ? <CircularProgress size={17} color="inherit" /> : undefined}
                                >
                                    Complete comparison
                                </Button>
                            </Stack>
                        </Paper>
                    )}
                </Stack>
            )}
        </Box>
    );
}
